import React, { useContext, useEffect } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faArrowLeft, faHome, faPaw, faTruck, faCrown } from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { routes } from "../config/utility";

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const { user, updateContext } = useContext(UserContext);

  const isWalkPayment = queryParams.get("walk_payment") === "true";
  const isTaxiPayment = queryParams.get("taxi_payment") === "true";
  const isMembershipPayment = queryParams.get("membership_payment") === "true";

  // Call updateContext only once when membership is purchased
  useEffect(() => {
    if (isMembershipPayment) {
      updateContext();
      // Auto redirect to dashboard after 3 seconds for membership payment
      const timer = setTimeout(() => {
        if (user?.isAuthenticated) {
          navigate(routes.dashboard);
        } else {
          navigate(routes.login);
        }
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isMembershipPayment, updateContext, navigate, user]);

  // Determine the content based on the payment type
  let title = "Payment Successful! 🎉";
  let message = "Thank you for your payment. Your transaction has been completed successfully.";
  let backButtonText = "Back to Dashboard";
  let backButtonIcon = faHome;
  let backButtonRoute = routes.dashboard;

  if (isWalkPayment) {
    message = "Your pet's walk is now confirmed!  A dedicated walker will be on the way soon. Get ready for happy paws! 🐾";
    backButtonText = "Back to Walks";
    backButtonIcon = faPaw;
    backButtonRoute = routes.walk;
  } else if (isTaxiPayment) {
    message = "Your pet taxi is booked! 🚖 A safe and comfortable ride is now scheduled for your furry friend. Sit back and relax!";
    backButtonText = "Back to Delivery";
    backButtonIcon = faTruck;
    backButtonRoute = routes.delivery;
  } else if (isMembershipPayment) {
    message = "Welcome to the pack! 🐶🎉 Thank you for subscribing to our monthly membership. Your pet's adventures just got even better!";
    backButtonText = "Go to Dashboard";
    backButtonIcon = faCrown;
    backButtonRoute = routes.dashboard;
  }

  const handleBackClick = () => {
    if (user?.isAuthenticated) {
      navigate(backButtonRoute);
    } else {
      // If not authenticated, redirect to login
      navigate(routes.login);
    }
  };

  // Card animation: pop-in with 3D effect
  const cardVariants = {
    hidden: { opacity: 0, scale: 0.3, rotateY: -90 },
    visible: {
      opacity: 1,
      scale: 1,
      rotateY: 0,
      transition: { type: "spring", stiffness: 100, damping: 10, delay: 0.2 },
    },
  };

  // Icon animation: bounce-in effect
  const iconVariants = {
    hidden: { scale: 0 },
    visible: {
      scale: [0, 1.5, 1],
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  // Text animation: fade-in
  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: 0.5 },
    },
  };

  // Button animation
  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: 0.8 },
    },
  };

  // Floating confetti animation
  const confettiPieces = Array.from({ length: 25 }).map((_, i) => (
    <motion.div
      key={i}
      className="w-2 h-2 rounded-full bg-primary absolute"
      initial={{ opacity: 0, y: -50 }}
      animate={{
        opacity: [0, 1, 0],
        y: [0, 150, 300],
        x: [0, Math.random() * 100 - 50, Math.random() * 100 - 50],
      }}
      transition={{
        duration: 3 + Math.random(),
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "loop",
        delay: Math.random() * 1.5,
      }}
      style={{
        top: "-10%",
        left: `${Math.random() * 100}%`,
      }}
    />
  ));

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden bg-gradient-to-br from-gradientStart to-gradientEnd">
      {/* Background confetti effect */}
      {confettiPieces}

      <motion.div
        className="bg-white rounded-3xl shadow-2xl p-10 text-center max-w-md w-full mx-4"
        style={{ perspective: 1000 }}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Success Icon */}
        <motion.div variants={iconVariants} initial="hidden" animate="visible">
          <FontAwesomeIcon icon={faCheckCircle} className="text-5xl text-green-500 mb-4" />
        </motion.div>

        {/* Success Title */}
        <motion.h1 variants={textVariants} className="text-3xl font-bold text-dark mb-2">
          {title}
        </motion.h1>

        {/* Success Message */}
        <motion.p variants={textVariants} className="text-body text-lg mb-8">
          {message}
        </motion.p>

        {/* Auto redirect message for membership */}
        {isMembershipPayment && (
          <motion.p
            variants={textVariants}
            className="text-sm text-gray-500 mb-4"
          >
            Redirecting to dashboard in 3 seconds...
          </motion.p>
        )}

        {/* Back Button */}
        <motion.button
          variants={buttonVariants}
          initial="hidden"
          animate="visible"
          onClick={handleBackClick}
          className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-full font-semibold transition-colors duration-200 flex items-center justify-center gap-2 mx-auto"
        >
          <FontAwesomeIcon icon={backButtonIcon} />
          {backButtonText}
        </motion.button>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
